/**
 * Dropdown de navigation accessible (clavier, ARIA, focus)
 * Utilisé dans le header pour les sous-menus
 */

import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';

/**
 * @param {Object} props
 * @param {string} props.label - Libellé du bouton déclencheur
 * @param {Array} props.items - Items du menu. Chaque item est soit:
 *   - { to: string, label: string } pour un lien
 *   - { sectionLabel: string, items: Array<{to, label}> } pour un groupe
 * @param {string} [props.className] - Classe CSS optionnelle
 * @param {React.Component} [props.LinkComponent] - Composant à utiliser pour les liens (défaut: Link)
 */
export default function NavDropdown({ label, items, className = '', LinkComponent = Link }) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);
  const menuId = `nav-dropdown-${label.replace(/\s+/g, '-').toLowerCase()}`;

  const close = () => setIsOpen(false);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        close();
      }
    };
    const handleEscape = (e) => {
      if (e.key === 'Escape') close();
    };
    if (isOpen) {
      document.addEventListener('click', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
      return () => {
        document.removeEventListener('click', handleClickOutside);
        document.removeEventListener('keydown', handleEscape);
      };
    }
  }, [isOpen]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setIsOpen((prev) => !prev);
    }
    if (e.key === 'ArrowDown' && !isOpen) {
      e.preventDefault();
      setIsOpen(true);
    }
  };

  return (
    <div className={`nav-dropdown ${isOpen ? 'is-open' : ''} ${className}`.trim()} ref={containerRef}>
      <button
        type="button"
        className="nav-dropdown-trigger"
        onClick={() => setIsOpen((prev) => !prev)}
        onKeyDown={handleKeyDown}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-controls={menuId}
        id={`${menuId}-trigger`}
      >
        {label}
        <span className="nav-dropdown-arrow" aria-hidden="true">▼</span>
      </button>
      {isOpen && (
        <ul
          id={menuId}
          role="menu"
          className="nav-dropdown-menu"
          aria-labelledby={`${menuId}-trigger`}
        >
          {items.map((item, idx) => {
            if (item.sectionLabel && item.items) {
              return (
                <li key={item.sectionLabel} role="none" className="nav-dropdown-section">
                  <span className="nav-dropdown-section-label" role="presentation">
                    {item.sectionLabel}
                  </span>
                  <ul role="group" className="nav-dropdown-group">
                    {item.items.map((sub) => (
                      <li key={sub.to} role="none">
                        <LinkComponent
                          to={sub.to}
                          role="menuitem"
                          className="nav-dropdown-item"
                          onClick={close}
                        >
                          {sub.label}
                        </LinkComponent>
                      </li>
                    ))}
                  </ul>
                </li>
              );
            }
            return (
              <li key={item.to || idx} role="none">
                <LinkComponent
                  to={item.to}
                  role="menuitem"
                  className="nav-dropdown-item"
                  onClick={close}
                >
                  {item.label}
                </LinkComponent>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
